import { useTranslation } from 'react-i18next';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchableSelect';
import { DurationInput } from '@/components/ui/durationInput';
import type { SettingField } from '@/api/treatmentLibrary/treatmentLibraryApi.types';
import { resolveLocalizedName } from '@/components/practitioner/TreatmentLibrary/localizedName';
import {
  parseDeviceSettingDurationValue,
  serializeDeviceSettingDuration,
  type DurationUnit,
} from '@/utils/durationFormatting';

const DEFAULT_DURATION_UNIT: DurationUnit = 'min';
const INPUT_CLASS_NAME =
  'mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo';

function resolveFallbackDurationUnit(unit: string): DurationUnit {
  return unit === 's' || unit === 'min' || unit === 'h' ? unit : DEFAULT_DURATION_UNIT;
}

function findChoiceLabel(field: SettingField, value: string): string | null {
  const matchingOption = field.options.find(function matchesEitherLanguage(option) {
    return option.label === value || option.labelEn === value || option.labelPl === value;
  });
  return matchingOption === undefined ? null : matchingOption.label;
}

function buildChoiceOptions(field: SettingField, value: string): SearchableSelectOption[] {
  const choiceOptions = field.options.map(function toChoiceOption(option) {
    return { value: option.label, label: option.label };
  });
  const isUnlistedValue = value.length > 0 && findChoiceLabel(field, value) === null;
  return isUnlistedValue ? [{ value, label: value }, ...choiceOptions] : choiceOptions;
}

interface DeviceSettingFieldProps {
  field: SettingField;
  value: string;
  isDisabled: boolean;
  onChange: (nextValue: string) => void;
}

export function DeviceSettingField({
  field,
  value,
  isDisabled,
  onChange,
}: DeviceSettingFieldProps) {
  const { t } = useTranslation('patients');
  const unitLabel = field.unit.length > 0 ? ' (' + field.unit + ')' : '';
  const fieldName = resolveLocalizedName(field.nameEn, field.namePl);

  if (field.fieldType === 'duration') {
    const parsed = parseDeviceSettingDurationValue(value, resolveFallbackDurationUnit(field.unit));
    return (
      <label className="text-xs font-semibold text-zoomuted">
        {fieldName}
        <DurationInput
          amount={parsed.amount}
          unit={parsed.unit}
          isDisabled={isDisabled}
          ariaLabel={fieldName}
          className="mt-1"
          onChange={function changeDuration(nextAmount, nextUnit) {
            onChange(serializeDeviceSettingDuration(nextAmount, nextUnit));
          }}
        />
      </label>
    );
  }

  if (field.fieldType === 'choice') {
    const matchedLabel = findChoiceLabel(field, value);
    return (
      <label className="text-xs font-semibold text-zoomuted">
        {fieldName}
        {unitLabel}
        <div className="mt-1">
          <SearchableSelect
            ariaLabel={fieldName}
            value={matchedLabel === null ? value : matchedLabel}
            options={buildChoiceOptions(field, value)}
            placeholder={t('sessionNote.chooseSettingOption')}
            isClearable
            isDisabled={isDisabled}
            onChange={onChange}
          />
        </div>
      </label>
    );
  }

  return (
    <label className="text-xs font-semibold text-zoomuted">
      {fieldName}
      {unitLabel}
      <input
        type={field.fieldType === 'number' ? 'number' : 'text'}
        inputMode={field.fieldType === 'number' ? 'decimal' : undefined}
        step={field.fieldType === 'number' ? 'any' : undefined}
        value={value}
        disabled={isDisabled}
        onChange={function changeSetting(event) {
          onChange(event.target.value);
        }}
        className={INPUT_CLASS_NAME}
      />
    </label>
  );
}
